import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';
import { useAuth } from '../contexts';
import { toast } from 'react-toastify';
import {
  authPageStyles,
  authInputStyles,
  authButtonStyles,
  authCheckboxStyles,
  authIconButtonStyles,
  authOptionsStyles,
  authLinkStyles,
  authLinkTextStyles,
  welcomeSectionStyles,
  welcomeContentStyles,
  welcomeTitleStyles,
  welcomeDescriptionStyles,
  decorativeCircle1Styles,
  decorativeCircle2Styles,

} from '../../../assets/styles';

// Validation schema
const loginSchema = Yup.object({
  identifier: Yup.string()
    .min(3, 'Email hoặc tên đăng nhập phải có ít nhất 3 ký tự')
    .required('Email hoặc tên đăng nhập là bắt buộc'),
  password: Yup.string()
    .min(6, 'Mật khẩu phải có ít nhất 6 ký tự')
    .required('Mật khẩu là bắt buộc'),
  remember: Yup.boolean(),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const formik = useFormik({
    initialValues: {
      identifier: '',
      password: '',
      remember: false,
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      setError('');
      try {

        localStorage.removeItem('savedIdentifier');
        localStorage.removeItem('savedPassword');

        const userData = await login({
          identifier: values.identifier,
          password: values.password,
          remember: values.remember
        });

        toast.success('Đăng nhập thành công!');

        if (userData?.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/home');
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'Đăng nhập thất bại';
        setFieldError('identifier', msg);
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box sx={authPageStyles.container}>
      <Container maxWidth="md">
        <Box sx={authPageStyles.mainBox}>
          {/* Wrap sections in a responsive row so the welcome section appears on the right */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: 3 }}>
            {/* FORM ĐĂNG NHẬP */}
            <Box sx={[authPageStyles.formSection, { flex: 1 }]}>
              <Box sx={authPageStyles.headerSection}>
                <Typography variant="h4" sx={authPageStyles.logoText}>
                  Quản lý GPT Team
                </Typography>
                <Typography variant="h6" sx={authPageStyles.pageTitle}>
                  Đăng nhập
                </Typography>
                <Typography variant="body2" sx={authPageStyles.subtitle}>
                  Vui lòng nhập thông tin để tiếp tục
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={authPageStyles.alert}>
                  {error}
                </Alert>
              )}

              <Box component="form" id="login-form" onSubmit={formik.handleSubmit}>
                {/* IDENTIFIER (EMAIL/USERNAME) */}
                <TextField
                  fullWidth
                  id="identifier"
                  name="identifier"
                  label="Email hoặc Tên đăng nhập"
                  placeholder="example@mail.com hoặc username"
                  value={formik.values.identifier}
                  onChange={formik.handleChange}
                  autoComplete='username' //Cho phép browser autofill
                  error={formik.touched.identifier && Boolean(formik.errors.identifier)}
                  helperText={formik.touched.identifier && formik.errors.identifier}
                  sx={authInputStyles}
                />

                {/* PASSWORD */}
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Mật khẩu"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  autoComplete='current-password' //Cho phép browser autofill
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={authIconButtonStyles}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={authInputStyles}
                />

                {/* OPTIONS */}
                <Box sx={authOptionsStyles}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="remember"
                        name="remember"
                        checked={formik.values.remember}
                        onChange={formik.handleChange}
                        sx={authCheckboxStyles}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={authLinkTextStyles}>
                        Ghi nhớ mật khẩu
                      </Typography>
                    }
                  />
                  <Link to="/forgot-password" style={authLinkStyles}>
                    Quên mật khẩu?
                  </Link>
                </Box>

                {/* SUBMIT BUTTON */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={formik.isSubmitting}
                  startIcon={formik.isSubmitting ? <CircularProgress size={20} /> : <LoginIcon />}
                  sx={authButtonStyles}
                >
                  {formik.isSubmitting ? 'Đang đăng nhập...' : 'Đăng nhập'}
                </Button>

                {/* REGISTER LINK */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={authLinkTextStyles}>
                    Chưa có tài khoản?{' '}
                    <Link to="/register" style={{ ...authLinkStyles, fontWeight: 600 }}>
                      Đăng ký ngay
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* PHẦN CHÀO MỪNG BÊN DƯỚI - sẽ xuất hiện bên phải trên màn hình lớn */}
            <Box sx={[welcomeSectionStyles, { flex: 1 }]}>
              <Box sx={welcomeContentStyles}>
                <Typography variant="h4" sx={welcomeTitleStyles}>
                  Chào mừng<br />trở lại!
                </Typography>
                <Typography variant="body1" sx={welcomeDescriptionStyles}>
                  Hệ thống quản lý GPT Team - Nơi quản lý và điều phối tài khoản hiệu quả. Hãy tiếp tục hành trình của bạn cùng chúng tôi.
                </Typography>
              </Box>

              {/* Decorative circles */}
              <Box sx={decorativeCircle1Styles} />
              <Box sx={decorativeCircle2Styles} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;